import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import apiService from '../../services/apiService';
import { showToast } from '../../components/Toast';
import ConfirmModal from '../../components/ConfirmModal';
import EndMembershipSheet from '../../components/membership/EndMembershipSheet';
import MemberPicker from '../../components/membership/MemberPicker';
import RecordCashSheet from '../../components/membership/RecordCashSheet';
import { DSButton, DSIcon, DSAlert } from '../../design-system/primitives';
import './membership.css';

// The single prioritised work queue. Every row is something a human must decide.
//
// Two rules from the requirements hold across the whole screen and shape it more
// than the layout does:
//
//   - Nothing here expires or removes anyone. Membership is granted
//     automatically but never revoked automatically, so no row may imply a
//     countdown, a deadline or a cutoff.
//   - Nothing fails silently. A payment that cannot be attributed appears here
//     rather than being dropped with a log line, which is what used to happen.
//
// An empty queue is a genuine success state, not a screen that failed to load.


// Exactly the actions the wireframe defines, in its wording.
//
// There is deliberately no snooze and no ignore. Nothing in this queue expires
// or removes anyone, so a row is cleared by dealing with it, not by hiding it -
// and a hidden row is a problem nobody is looking at any more.
const ACTION_LABEL = {
  open_in_square: 'Open in Square',
  end_membership: 'End membership',
  record_payment: 'Record payment',
  open_onboarding: 'Open onboarding',
  grant_membership_anyway: 'Grant membership anyway',
  close_application: 'Close application',
  assign_to_member: 'Assign to a member',
  mark_duplicate: 'It is a duplicate',
  count_as_membership: 'Count as membership',
  not_a_membership_payment: 'Not a membership payment',
  link_to_member: 'Link to existing member',
  merge: 'Merge',
  not_the_same_person: 'Not the same person',
  open_checklist: 'Open checklist',
  cancel_offboarding: 'Cancel offboarding'
};

const PRIMARY_ACTIONS = new Set([
  'open_in_square', 'record_payment', 'open_onboarding', 'assign_to_member',
  'link_to_member', 'count_as_membership', 'merge', 'open_checklist'
]);

// Reserved for the ones that change somebody's standing at the club.
const DANGER_ACTIONS = new Set(['end_membership', 'close_application']);

const GROUP_TONE = {
  offboarding: 'brand',
  payment: 'warning',
  onboarding: 'info',
  money: 'primary',
  data: 'error'
};

function actionTone(action) {
  if (DANGER_ACTIONS.has(action)) return 'danger';
  return PRIMARY_ACTIONS.has(action) ? 'primary' : 'secondary';
}

function NeedsAttention({ onCountChange }) {
  const navigate = useNavigate();

  const [queue, setQueue] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busyKey, setBusyKey] = useState(null);

  const [endingFor, setEndingFor] = useState(null);
  const [cashFor, setCashFor] = useState(null);
  const [pickerFor, setPickerFor] = useState(null);
  const [confirmState, setConfirmState] = useState(null);

  const load = useCallback(async () => {
    try {
      setError(null);
      const response = await apiService.getMembershipQueue();
      setQueue(response.data);
      if (onCountChange) onCountChange(response.data.total);
    } catch (err) {
      setError(err.response?.data?.error || 'Could not load the work queue');
    } finally {
      setLoading(false);
    }
  }, [onCountChange]);

  useEffect(() => { load(); }, [load]);

  const run = async (item, work, successMessage) => {
    setBusyKey(item.key);
    try {
      await work();
      if (successMessage) showToast(successMessage, 'success');
      await load();
    } catch (err) {
      showToast(err.response?.data?.error || 'That did not work', 'error');
    } finally {
      setBusyKey(null);
    }
  };

  const handleAction = (item, action) => {
    switch (action) {
      // A judgement about the pair, recorded against both accounts. It used to
      // file a seven-day snooze, which brought relatives who share a phone
      // number back every week having already been ruled on.
      case 'not_the_same_person':
        return run(item,
          () => apiService.markNotDuplicates(item.userIds),
          'Noted. These two will not be raised as a duplicate again');

      case 'mark_duplicate':
        return run(item, () => apiService.dismissQueueItem(item.key), 'Noted, this will not come back');

      case 'close_application':
        return setConfirmState({
          title: `Close ${item.subject}'s application?`,
          message: 'The onboarding checklist stops here and the application moves to history. Reopening means filing a new one.',
          confirmLabel: 'Close application',
          danger: true,
          onConfirm: () => run(item,
            () => apiService.put(`/onboarding/membership-status/${item.subjectUserId}`, { status: 'inactive' }),
            'Application closed')
        });

      case 'end_membership':
        return setEndingFor({
          userId: item.subjectUserId, name: item.subject, method: item.method, endsOn: item.endsOn
        });

      case 'record_payment':
        return setCashFor({ userId: item.subjectUserId, name: item.subject, detail: item.detail });

      case 'open_in_square':
      case 'open_onboarding':
      case 'open_checklist':
        return navigate(`/users/${item.subjectUserId}`);

      case 'grant_membership_anyway':
        return setConfirmState({
          title: 'Grant membership now?',
          // States what actually happens rather than warning about consequences
          // that do not occur. Onboarding stays outstanding and visible.
          message: `${item.subject} becomes a member straight away, before onboarding is finished. The outstanding onboarding steps stay on their record.`,
          confirmLabel: 'Grant membership',
          onConfirm: () => run(item,
            () => apiService.grantOnboardingOverride(item.subjectUserId),
            'Membership granted')
        });

      case 'assign_to_member':
        return setPickerFor({
          item,
          title: 'Who does the extra subscription cover?',
          // Sponsorship is admin-only and must never surface to the member being
          // sponsored, so nothing here notifies anyone.
          description: `${item.subject} is paying for more than one membership. Choose the member the extra subscription covers.`,
          excludeUserId: item.subjectUserId,
          onPick: (user) => run(item,
            () => apiService.linkSponsorship(user.id, item.subjectUserId),
            `${user.first_name || user.email} is now covered by ${item.subject}`)
        });

      case 'link_to_member':
        return setPickerFor({
          item,
          title: 'Link this Square customer',
          description: 'Choose the member this Square customer belongs to. Their payments will attach to that account.',
          onPick: (user) => run(item,
            () => apiService.linkSquareCustomer(item.squareCustomerId, user.id),
            'Square customer linked')
        });


      case 'count_as_membership':
        return setConfirmState({
          title: 'Count this as a membership payment?',
          // The difference between the two crediting actions is the whole point
          // of this row, so the copy has to carry it.
          message: 'This payment is credited, and the Square item it was paid against is added to the membership configuration. Every future payment against that item counts automatically.',
          confirmLabel: 'Count as membership',
          onConfirm: () => run(item,
            () => apiService.categorisePayment(item.transactionId, 'membership', item.subjectUserId),
            'Counted, and the Square item is now configured')
        });

      case 'not_a_membership_payment':
        return run(item,
          () => apiService.categorisePayment(item.transactionId, 'not_membership'),
          'Removed from the queue');

      case 'merge':
        // The merge screen lives on the Users page, not under /membership.
        // This pointed at a route that has never existed, so the one action on
        // the one item type the merge tooling was built for led to Page not
        // found.
        //
        // Lower id as the target, so the older account is the one that
        // survives - it is the one with the longer history and the address
        // people already have. The admin can swap them on the screen.
        return navigate(`/users/merge?source=${item.userIds[1]}&target=${item.userIds[0]}`);

      case 'cancel_offboarding':
        return setConfirmState({
          title: 'Keep this membership?',
          message: `${item.subject} stays a member, and the scheduled ending is cancelled.`,
          confirmLabel: 'Keep membership',
          onConfirm: () => run(item,
            () => apiService.cancelEndMembership(item.subjectUserId),
            'Membership kept')
        });

      default:
        return undefined;
    }
  };


  const completeStep = (item, stepKey) => run(item,
    () => apiService.completeOffboardingStep(item.key.split(':')[1], stepKey),
    'Step marked done');

  if (loading) return <div className="loading">Loading...</div>;

  if (error) {
    return <DSAlert variant="error">{error}</DSAlert>;
  }

  return (
    <div className="membership-queue">
      {queue.total === 0 ? (
        <div className="membership-queue__empty">
          <DSIcon name="checkCircle" size="lg" tone="success" />
          <h3>Nothing needs attention</h3>
          <p>
            Every payment is matched to a person and every member's payment is healthy.
            New problems appear here on their own.
          </p>
        </div>
      ) : (
        queue.groups.map((group) => (
          <section key={group.key} className="membership-queue__group">
            {/* Dot, title, count, then a rule to the edge. The dot carries the
                group's tone so the eye can find the offboarding block or the
                money block without reading the headings. */}
            <div className="membership-queue__group-head">
              <span className={`membership-queue__dot membership-queue__dot--${GROUP_TONE[group.key] || 'brand'}`} />
              <span className="membership-queue__group-title">{group.title}</span>
              <span className="membership-queue__group-count">{group.items.length}</span>
              <span className="membership-queue__rule" />
            </div>

            {group.items.map((item) => (
              <article key={item.key} className="membership-queue__row">
                <div className="membership-queue__body">
                  <div className="membership-queue__heading">
                    <span className="membership-queue__subject">{item.subject}</span>
                    {item.kindLabel && (
                      <span className="membership-queue__kind">{item.kindLabel}</span>
                    )}
                  </div>

                  {/* What happened and when, in plain language. Never a status code. */}
                  <p className="membership-queue__detail">{item.detail}</p>
                  {item.meta && <p className="membership-queue__meta">{item.meta}</p>}

                  {item.steps && item.steps.length > 0 && (
                    <ul className="membership-queue__steps">
                      {item.steps.map((step) => (
                        <li key={step.key}>
                          <button
                            type="button"
                            className="membership-queue__step-box"
                            aria-label={`Mark done: ${step.label}`}
                            disabled={busyKey === item.key}
                            onClick={() => completeStep(item, step.key)}
                          />
                          <span className="membership-queue__step-label">{step.label}</span>
                          {/* Stopping a payment has to happen before the
                              renewal or it bills once more. Everything else is
                              due on the day the membership ends. */}
                          {step.now
                            ? <span className="membership-queue__step-now">Now</span>
                            : step.due && <span className="membership-queue__step-due">{step.due}</span>}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                <div className="membership-queue__actions">
                  {(item.actions || []).map((action) => (
                    <DSButton
                      key={action}
                      tone={actionTone(action)}
                      appearance={PRIMARY_ACTIONS.has(action) ? 'solid' : 'outline'}
                      size="sm"
                      disabled={busyKey === item.key}
                      onClick={() => handleAction(item, action)}
                    >
                      {ACTION_LABEL[action] || action}
                    </DSButton>
                  ))}
                </div>
              </article>
            ))}
          </section>
        ))
      )}


      {endingFor && (
        <EndMembershipSheet
          userId={endingFor.userId}
          name={endingFor.name}
          method={endingFor.method}
          endsOn={endingFor.endsOn}
          onClose={() => setEndingFor(null)}
          onDone={() => { setEndingFor(null); load(); }}
        />
      )}

      {cashFor && (
        <RecordCashSheet
          member={cashFor}
          onClose={() => setCashFor(null)}
          onDone={() => { setCashFor(null); load(); }}
        />
      )}

      {pickerFor && (
        <MemberPicker
          title={pickerFor.title}
          description={pickerFor.description}
          excludeUserId={pickerFor.excludeUserId}
          onClose={() => setPickerFor(null)}
          onPick={(user) => { const pick = pickerFor.onPick; setPickerFor(null); pick(user); }}
        />
      )}

      {confirmState && (
        <ConfirmModal
          title={confirmState.title}
          message={confirmState.message}
          confirmLabel={confirmState.confirmLabel}
          danger={confirmState.danger}
          onConfirm={() => { const go = confirmState.onConfirm; setConfirmState(null); go(); }}
          onCancel={() => setConfirmState(null)}
        />
      )}
    </div>
  );
}

export default NeedsAttention;
